import {
  V2PowerRoomConfigCircuitAlarmListWithRoomIdPostResponse,
  V2PowerRoomConfigCircuitDataPostResponse,
  V2PowerRoomConfigCircuitPagePostResponse,
  V2PowerRoomConfigElectricityConsumptionSituationPostResponse,
  V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import type { Graph } from '@antv/g6';
import { isNil } from 'lodash-es';
import _ from 'lodash';
import { formatType, getNodeSize } from './components/WiringDiagramHighV/drawFunc';

export enum DisplayMode {
  Graph = 1,
  Table = 2,
}

export type CircuitTableItem = Exclude<V2PowerRoomConfigCircuitPagePostResponse['list'], undefined>[number];

export type TimeDataItem = Exclude<
  V2PowerRoomConfigElectricityConsumptionSituationPostResponse['circuitElectricityConsumptionSituationInfoList'],
  undefined
>[number];

export type AlarmItem = Exclude<V2PowerRoomConfigCircuitAlarmListWithRoomIdPostResponse['list'], undefined>[number];

export type CircuitItem = Exclude<V2PowerRoomConfigCircuitPagePostResponse['list'], undefined>[number];

export type CircuitTimeDataItem = Exclude<
  V2PowerRoomConfigCircuitDataPostResponse['circuitDataInfoList'],
  undefined
>[number];
// 1是合闸 2是分闸
export enum SwitchState {
  NONE = 0,
  OPEN = 1,
  CLOSED = 2,
}

export const SwitchStateDisplay = {
  [SwitchState.NONE]: '--',
  [SwitchState.OPEN]: '合闸',
  [SwitchState.CLOSED]: '开闸',
};

export enum DataProperty {
  Power,
  Current,
  Voltage,
}

export const DataPropertyDisplay = {
  [DataProperty.Power]: '有功功率',
  [DataProperty.Current]: '电流',
  [DataProperty.Voltage]: '线电压',
};

export function goFullscreen(element: HTMLDivElement) {
  if (document.fullscreenElement) {
    return Promise.reject(new Error('全屏模式被禁用'));
  }
  let result = null;
  if (element.requestFullscreen) {
    result = element.requestFullscreen();
  }
  return result || Promise.reject(new Error('不支持全屏'));
}
export function cancelFullscreen() {
  document.exitFullscreen?.();
}

interface PaddingOptions {
  left?: number;
  top?: number;
  bottom?: number;
}

export function setPadding(graph: Graph, { left = 0, top = 0, bottom = 0 }: PaddingOptions) {
  // 确保已经渲染过
  graph.render();

  // 获取所有节点的包围盒
  const nodes = graph.getNodes();
  if (!nodes.length) return;

  let minX = Infinity,
    minY = Infinity,
    maxY = -Infinity;
  nodes.forEach(node => {
    const bbox = node.getBBox();
    minX = Math.min(minX, bbox.minX);
    minY = Math.min(minY, bbox.minY);
    maxY = Math.max(maxY, bbox.maxY);
  });
  // 平移，保证左边和上边边距
  graph.translate(left - minX, top - minY);

  // 保证下边距，如果内容高度超出画布则扩高画布
  const canvasHeight = graph.getHeight();
  const contentHeight = maxY - minY;
  const neededHeight = contentHeight + top + bottom;

  if (neededHeight > canvasHeight) {
    graph.changeSize(graph.getWidth(), neededHeight);
  }
}

export const formatAllNodes = (nodes: any[], rowGap = 180) => {
  // 1. 按 row 分组
  const rowMap: Record<string, any[]> = {};
  nodes.forEach(node => {
    const key = String(node.lineIndex);
    if (!rowMap[key]) rowMap[key] = [];
    rowMap[key].push(node);
  });
  const rowKeys = Object.keys(rowMap).sort((a, b) => Number(a) - Number(b));
  // 全部起始从y为0开始
  rowKeys.forEach(rowKey => {
    const rowNodes = rowMap[rowKey];
    const minNode = getMinYNode(rowNodes);
    const minY = _.cloneDeep(minNode.y);
    if (minY < 0) {
      rowNodes.forEach(node => {
        node.y = (node.y ?? 0) + Math.abs(minY ?? 0);
      });
    } else {
      rowNodes.forEach(node => {
        node.y = (node.y ?? 0) - (minY ?? 0);
      });
    }
  });

  let currentYShift = 0; // 当前累计下移量
  rowKeys.forEach((rowKey, index) => {
    const rowNodes = rowMap[rowKey];
    if (index === 0) {
      // 第一行保持原始 y，不做修改
      return;
    }

    // 找到上一行的最大高度
    const prevRowNodes = rowMap[rowKeys[index - 1]];
    const maxNode = getMaxYNode(prevRowNodes);
    const minNode = getMinYNode(prevRowNodes);
    // 当前行最小y节点
    const currentMinNode = getMinYNode(rowNodes);
    const prevMaxHeight =
      (maxNode.y ?? 0) + (!isNil(maxNode?.size?.[1]) ? maxNode?.size?.[1] / 2 : 0) - (minNode.y ?? 0);
    // 更新当前行的 y
    rowNodes.forEach(node => {
      node.y = (node.y ?? 0) + currentYShift + prevMaxHeight + rowGap + currentMinNode?.size?.[1] / 2;
    });

    // 累加
    currentYShift += prevMaxHeight + rowGap + currentMinNode?.size?.[1] / 2;
  });

  return nodes;
};

function getMaxYNode(nodes: any[]) {
  if (!nodes.length) return null;
  return nodes.reduce((maxNode, node) => ((node.y ?? 0) > (maxNode.y ?? 0) ? node : maxNode));
}

function getMinYNode(nodes: any[]) {
  if (!nodes.length) return null;
  return nodes.reduce((minNode, node) => ((node.y ?? Infinity) < (minNode.y ?? Infinity) ? node : minNode));
}

// 分行创建连线
export const formatAllEdges = (nodes: any[], graph: Graph) => {
  const edges: any[] = [];
  // 1. 按 row 分组
  const rowMap: Record<string, any[]> = {};
  nodes.forEach(node => {
    const key = String(node.lineIndex);
    if (!rowMap[key]) rowMap[key] = [];
    rowMap[key].push(node);
  });
  const rowKeys = Object.keys(rowMap).sort((a, b) => Number(a) - Number(b));
  // 全部起始从y为0开始
  rowKeys.forEach(rowKey => {
    // 最后一行不需要连线
    // if (Number(rowKey) < rowKeys.length - 1) {
    const currentRowNodes = rowMap[rowKey].filter(i => i.isOneLine);

    const nextConnectNode = currentRowNodes[0];

    if (nextConnectNode?.value?.fromCircuitId) {
      const findConnectPreNode = nodes
        .filter(i => i?.isLastNode)
        .find(i => i.id === nextConnectNode?.value?.fromCircuitId?.toString());

      if (findConnectPreNode) {
        let verticalHight = 0;
        // 如果是高压
        if (findConnectPreNode?.isHigh) {
          // 同一行且在同一水平线上的
          const preSameLineNodes = nodes.filter(
            i =>
              i.lineIndex === findConnectPreNode.lineIndex &&
              i.y - i.size[1] / 2 === findConnectPreNode.y - findConnectPreNode.size[1] / 2
          );

          preSameLineNodes.forEach(node => {
            const [_, nodeHeight] = getNodeSize(formatType(node?.type!, node?.value?.cabinetType, undefined));
            if (nodeHeight > verticalHight) {
              verticalHight = nodeHeight;
            }
          });
        }
        // 如果是低压的，就要知道有几层
        if (!findConnectPreNode?.isHigh) {
          const lowSameLineNodes = nodes.filter(
            i =>
              i.lineIndex === findConnectPreNode.lineIndex &&
              i.y - i.size[1] / 2 >= findConnectPreNode.y - findConnectPreNode.size[1] / 2
          );
          verticalHight = getMaxGroupSum(lowSameLineNodes, 'x', 1);
          // console.log('maxHeight', maxHeight);
          // verticalHight = lowSameLineNodes.slice(1).reduce((sum, item) => sum + item.size[1], 0);
        }

        const controlPoints = [
          {
            x: findConnectPreNode.x + findConnectPreNode.size[0] / 2 + 50,
            y: findConnectPreNode.y - findConnectPreNode.size[1] / 2,
          },
          {
            x: findConnectPreNode.x + findConnectPreNode.size[0] / 2 + 50,
            y: findConnectPreNode.y - findConnectPreNode.size[1] / 2 + verticalHight + 80,
          },
          {
            x: nextConnectNode.x - nextConnectNode.size[0] / 2 - 50,
            y: findConnectPreNode.y - findConnectPreNode.size[1] / 2 + verticalHight + 80,
          },
          {
            x: nextConnectNode.x - nextConnectNode.size[0] / 2 - 50,
            y: nextConnectNode.y - nextConnectNode.size[1] / 2,
          },
        ];

        const edge = createTopRightToTopLeftEdge(findConnectPreNode.id, nextConnectNode.id, controlPoints);
        edges.push(edge);
      }
    }
  });
  return edges;
};

function createTopRightToTopLeftEdge(
  sourceId: string,
  targetId: string,
  controlPoints: {
    x: any;
    y: any;
  }[]
) {
  return {
    source: sourceId,
    target: targetId,
    sourceAnchor: 0, // 右上角
    targetAnchor: 1, // 左上角
    type: 'polyline',
    style: {
      stroke: 'red',
      lineDash: [5, 3], // 虚线
      lineWidth: 4,
    },
    controlPoints,
    router: null,
  };
}

export const formatData = (data?: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse) => {
  let tempDataList: { id: number; type: string; value: any }[] = [];
  const loopData = (data: V2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPostResponse['list']) => {
    data?.forEach(item => {
      item.primaryWiringDiagramInfo?.forEach(i => {
        i.next?.forEach(c => {
          tempDataList.push({
            id: c.id!,
            type: c.type!,
            value: {
              ...c.value,
              cabinetName: i.value?.name,
            },
          });
        });
      });
      item.lowWiringDiagramInfo?.forEach(i => {
        i.next?.forEach(c => {
          tempDataList.push({
            id: c.id!,
            type: c.type!,
            value: {
              ...c.value,
              cabinetName: i.value?.name,
            },
          });
        });
      });
    });
  };
  loopData(data?.list);
  return tempDataList;
};

export function getMaxGroupSum(arr: any[], groupKey: string, valueIndex: number) {
  const groupMap = new Map();

  // 按 groupKey 分组并累加 value[index]
  arr.forEach(item => {
    const groupVal = item[groupKey];
    const targetValue = item.size?.[valueIndex] || 0;
    groupMap.set(groupVal, (groupMap.get(groupVal) || 0) + targetValue);
  });

  // 找出最大值
  let maxGroup = null;
  let maxSum = -Infinity;
  for (const [key, sum] of groupMap) {
    if (sum > maxSum) {
      maxSum = sum;
      maxGroup = key;
    }
  }

  return maxSum;
}
